// ============================================================================
// MÓDULO 7 - UNIDAD 3: EVERYDAY ACTIVITIES
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Phrasal Verbs (Verb + Particle)
// Key Rule: Only the VERB changes, particle stays the same
// Vocabulario: work out, chill out, meet up, turn up, wake up, etc.
// Contexto: Daily activities with phrasal verbs
// ============================================================================

export const M7_UNIT_3 = {
  id: "m7-u3",
  title: "Unit 3: Everyday Activities",
  topic: "Master essential phrasal verbs, understand their structure, and use them naturally across different tenses!",
  
  grammarImage: "/images/grammar/m7-u3-phrasal-verbs.jpg",
  grammarText:"particle (preposition or adverb) that create new meanings.",
  iconType: "running",

  sets: [
    // ========================================================================
    // SET 1: PHRASAL VERB STRUCTURE - BASIC UNDERSTANDING
    // ========================================================================
    {
      id: "m7-u3-s1",
      title: "Set 1: Understanding Phrasal Verb Structure",
      questions: [
        {
          type: "mc",
          q: "A phrasal verb consists of:",
          options: [
            "A verb + a particle (preposition or adverb)",
            "Two verbs together",
            "A verb with -ing"
          ],
          a: "A verb + a particle (preposition or adverb)",
          tip: "🔧 PHRASAL VERB STRUCTURE!\n\nPhrasal verb = VERB + PARTICLE\n\nExamples:\n• 'wake' (verb) + 'up' (particle) = wake up\n• 'work' (verb) + 'out' (particle) = work out\n\nThe particle changes the meaning:\n• 'wake' = estar despierto\n• 'wake UP' = despertar (acción)\n\nTogether they create NEW meaning! 🎯",
          successtip: "✓ STRUCTURE UNDERSTOOD!\nVerb + Particle = Phrasal Verb 🔧\nBasic building block!"
        },
        {
          type: "mc",
          q: "In the phrasal verb 'work out', which part changes when conjugating?",
          options: [
            "Only 'work' changes (the verb)",
            "Only 'out' changes (the particle)",
            "Both change"
          ],
          a: "Only 'work' changes (the verb)",
          successtip: "✓ KEY RULE!\nOnly the VERB changes! 💪\nParticle stays the same always!"
        },
        {
          type: "mc",
          q: "Which is CORRECT for third person singular?",
          options: [
            "She wakes up early",
            "She wake ups early",
            "She waking up early"
          ],
          a: "She wakes up early",
          tip: "🚫 COMMON ERROR!\n\n❌ 'Wake ups' = WRONG! Never add -s to particle!\n✅ 'Wakes up' = CORRECT! Only verb gets -s!\n\nThe particle 'up' NEVER changes!\n\nPattern: He/She/It + VERB-s + particle\n\nThis is the #1 mistake learners make! 🎯",
          successtip: "✓ CONJUGATION CORRECT!\nWakes up (not wake ups) ✅\nOnly verb changes!"
        },
        {
          type: "blank",
          q: "Complete: 'My brother ___ at the gym every morning.' (works out / work outs)",
          a: "works out",
          accept: { ignoreCase: true },
          successtip: "✓ THIRD PERSON!\nWorks out (verb changes) 💪\nParticle 'out' unchanged!"
        },
        {
          type: "mc",
          q: "'Meet up' means:",
          options: [
            "To get together with someone",
            "To meet someone for the first time",
            "To go upstairs"
          ],
          a: "To get together with someone",
          successtip: "✓ PHRASAL MEANING!\nMeet up = reunirse 👥\nDifferent from just 'meet'!"
        },
        {
          type: "order",
          q: "Traduce: Ellos se reúnen cada viernes por la tarde",
          a: "They meet up every Friday afternoon",
          successtip: "✓ REGULAR MEETING!\nMeet up = phrasal verb ✅\nParticle 'up' adds casual meaning!"
        },
        {
          type: "mc",
          q: "Which is the INCORRECT form?",
          options: [
            "He get ups at 6 AM",
            "He gets up at 6 AM",
            "He is getting up at 6 AM"
          ],
          a: "He get ups at 6 AM",
          successtip: "✓ ERROR SPOTTED!\n'Get ups' = wrong ❌\n'Gets up' = correct ✅"
        },
        {
          type: "blank",
          q: "In past tense: 'Yesterday, we ___ at the new café.' (met up / meet upped)",
          a: "met up",
          accept: { ignoreCase: true },
          successtip: "✓ PAST TENSE!\nMet up (verb changes to past) ✅\nParticle 'up' stays same!"
        },
        {
          type: "mc",
          q: "'Turn up' can mean:",
          options: [
            "To arrive or appear",
            "To turn a page",
            "To go up in an elevator"
          ],
          a: "To arrive or appear",
          successtip: "✓ PHRASAL MEANING!\nTurn up = aparecer/llegar 🚪\nOften unexpected arrival!"
        },
        {
          type: "mc",
          q: "Complete: 'I need to ___ before the party.' Meaning: exercise",
          options: [
            "work out",
            "work off",
            "work in"
          ],
          a: "work out",
          successtip: "✓ EXERCISE VERB!\nWork out = ejercitarse 🏋️\nEssential phrasal verb!"
        }
      ]
    },

    // ========================================================================
    // SET 2: CONJUGATION FOCUS - NO PARTICLE CHANGES
    // ========================================================================
    {
      id: "m7-u3-s2",
      title: "Set 2: Mastering Phrasal Verb Conjugation",
      questions: [
        {
          type: "mc",
          q: "Which sentence shows CORRECT conjugation?",
          options: [
            "She always chills out on Sundays",
            "She always chill outs on Sundays",
            "She always chilling out on Sundays"
          ],
          a: "She always chills out on Sundays",
          tip: "✅ CHILL OUT!\n\n'Chill out' = relajarse\n\nConjugation:\n• I/You/We/They chill out\n• He/She/It chills out (verb gets -s)\n\nThe particle 'out' NEVER changes!\n\n❌ 'Chill outs' = common error\n✅ 'Chills out' = correct\n\nOnly verb changes! 😌",
          successtip: "✓ RELAXATION VERB!\nChills out = correct! 😌\nVerb conjugated, particle unchanged!"
        },
        {
          type: "blank",
          q: "Present continuous: 'Right now, he ___ with his friends.' (is chilling out / chills out)",
          a: "is chilling out",
          accept: { ignoreCase: true, alts: ["'s chilling out"] },
          successtip: "✓ CONTINUOUS FORM!\nIs chilling out = right now action! 😎\nBE + verb-ing + particle!"
        },
        {
          type: "mc",
          q: "Future tense: Which is correct?",
          options: [
            "We will meet up tomorrow",
            "We will meets up tomorrow",
            "We will meet upping tomorrow"
          ],
          a: "We will meet up tomorrow",
          successtip: "✓ FUTURE FORM!\nWill meet up = correct! 📅\nWill + base verb + particle!"
        },
        {
          type: "order",
          q: "Traduce: Ella se despertó temprano esta mañana",
          a: "She woke up early this morning",
          successtip: "✓ PAST TENSE!\nWoke up (irregular past) ⏰\nVerb changes, particle stays!"
        },
        {
          type: "mc",
          q: "Present perfect: Which is CORRECT?",
          options: [
            "I have worked out three times this week",
            "I have work outed three times this week",
            "I have working out three times this week"
          ],
          a: "I have worked out three times this week",
          tip: "💪 PRESENT PERFECT!\n\nStructure: have/has + past participle + particle\n\n✅ 'Have worked out' = correct\n'worked' = past participle of 'work'\n'out' = particle (unchanged)\n\n❌ 'Work outed' = WRONG! Never change particle!\n❌ 'Working out' = wrong form (that's continuous)\n\nParticle ALWAYS stays the same! 🎯",
          successtip: "✓ PERFECT TENSE!\nHave worked out = correct! 💪\nPast participle + particle!"
        },
        {
          type: "mc",
          q: "Which shows INCORRECT particle usage?",
          options: [
            "They turned ups late",
            "They turned up late",
            "They are turning up late"
          ],
          a: "They turned ups late",
          successtip: "✓ ERROR FOUND!\n'Turned ups' = wrong ❌\n'Turned up' = correct ✅\nParticle never gets -s!"
        },
        {
          type: "blank",
          q: "Negative form: 'She ___ on weekends.' (doesn't work out)",
          a: "doesn't work out",
          accept: { ignoreCase: true, alts: ["does not work out"] },
          successtip: "✓ NEGATIVE FORM!\nDoesn't work out = base form! 🚫\nDoesn't + base verb + particle!"
        },
        {
          type: "mc",
          q: "Question form: Which is CORRECT?",
          options: [
            "Does he wake up early?",
            "Does he wakes up early?",
            "Do he wakes up early?"
          ],
          a: "Does he wake up early?",
          successtip: "✓ QUESTION FORM!\nDoes + base verb + particle = correct! ❓\nDoes he wake up ✅"
        },
        {
          type: "order",
          q: "Traduce: ¿Estás haciendo ejercicio ahora mismo?",
          a: "Are you working out right now?",
          successtip: "✓ PRESENT CONTINUOUS QUESTION!\nAre you working out? 💪\nBE + subject + verb-ing + particle!"
        },
        {
          type: "mc",
          q: "Complete all blanks: 'I ___ every day, but my sister only ___ twice a week.' (work out / works out)",
          options: [
            "work out / works out",
            "works out / work out",
            "work out / work outs"
          ],
          a: "work out / works out",
          successtip: "✓ BOTH CONJUGATIONS!\nI work out / she works out = perfect! 🏋️\nFirst vs third person correct!"
        }
      ]
    },

    // ========================================================================
    // SET 3: WORK OUT & CHILL OUT - SPECIFIC VERBS
    // ========================================================================
    {
      id: "m7-u3-s3",
      title: "Set 3: Work Out & Chill Out",
      questions: [
        {
          type: "mc",
          q: "'Work out' means:",
          options: [
            "To exercise or do physical activity",
            "To work outside",
            "To finish work"
          ],
          a: "To exercise or do physical activity",
          tip: "💪 WORK OUT!\n\n'Work out' = ejercitarse/entrenar\n\nCommon uses:\n• 'I work out at the gym' = hago ejercicio\n• 'She works out every morning' = entrena\n• 'Let's work out together' = entrenemos juntos\n\nDifferent from 'work':\n• 'I work' = trabajo (empleo)\n• 'I work out' = hago ejercicio\n\nParticle 'out' changes the meaning completely! 🏋️",
          successtip: "✓ EXERCISE VERB!\nWork out = hacer ejercicio 💪\nEssential fitness vocabulary!"
        },
        {
          type: "blank",
          q: "I ___ at the gym three times a week. (work out)",
          a: "work out",
          accept: { ignoreCase: true },
          successtip: "✓ FITNESS ROUTINE!\nWork out = present simple ✅\nRegular exercise habit!"
        },
        {
          type: "mc",
          q: "'Chill out' means:",
          options: [
            "To relax or calm down",
            "To feel cold",
            "To go outside"
          ],
          a: "To relax or calm down",
          successtip: "✓ RELAXATION!\nChill out = relajarse 😌\nInformal, common phrasal verb!"
        },
        {
          type: "order",
          q: "Traduce: Él se relaja viendo películas los domingos",
          a: "He chills out watching movies on Sundays",
          successtip: "✓ WEEKEND RELAXATION!\nChills out = third person correct! 🎬\nVerb conjugated properly!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'After a stressful day, I just want to ___.'",
          options: [
            "chill out",
            "work out",
            "turn up"
          ],
          a: "chill out",
          successtip: "✓ NATURAL CHOICE!\nChill out after stress = perfect! 😌\nLogical context!"
        },
        {
          type: "blank",
          q: "Present continuous: 'She ___ at the park right now.' (is working out)",
          a: "is working out",
          accept: { ignoreCase: true, alts: ["'s working out"] },
          successtip: "✓ ONGOING ACTION!\nIs working out = happening now! 🏃‍♀️\nContinuous tense with phrasal verb!"
        },
        {
          type: "mc",
          q: "Which sentence uses 'work out' correctly in past tense?",
          options: [
            "I worked out for an hour yesterday",
            "I work outed for an hour yesterday",
            "I working out for an hour yesterday"
          ],
          a: "I worked out for an hour yesterday",
          successtip: "✓ PAST TENSE!\nWorked out = past simple ✅\nRegular past tense + particle!"
        },
        {
          type: "mc",
          q: "Complete: 'You need to ___ and stop worrying so much!'",
          options: [
            "chill out",
            "work out",
            "meet up"
          ],
          a: "chill out",
          tip: "😌 CHILL OUT USAGE!\n\n'Chill out' = relajarse/calmarse\n\nOften used as advice/command:\n• 'Chill out!' = ¡Relájate!\n• 'You should chill out' = Deberías relajarte\n• 'Let's chill out' = Relajémonos\n\nInformal, friendly tone!\nVery common in casual English! 🎯",
          successtip: "✓ ADVICE GIVEN!\nChill out = relax command! 😌\nNatural imperative use!"
        },
        {
          type: "order",
          q: "Traduce: ¿Vas a hacer ejercicio mañana?",
          a: "Are you going to work out tomorrow?",
          successtip: "✓ FUTURE PLAN!\nGoing to work out = future! 💪📅\nPhasal verb in future form!"
        },
        {
          type: "mc",
          q: "Final question: 'During the week I ___ intensely, but on weekends I ___.' Natural completion:",
          options: [
            "work out / chill out",
            "chill out / work out",
            "meet up / turn up"
          ],
          a: "work out / chill out",
          successtip: "✓ WEEKLY BALANCE!\nWork out (weekday) + chill out (weekend) = perfect balance! ⚖️\nNatural contrast!"
        }
      ]
    },

    // ========================================================================
    // SET 4: MORE WORK OUT & CHILL OUT PRACTICE
    // ========================================================================
    {
      id: "m7-u3-s4",
      title: "Set 4: Advanced Usage - Work Out & Chill Out",
      questions: [
        {
          type: "mc",
          q: "Which is the most natural question?",
          options: [
            "Do you work out regularly?",
            "Are you working out regularly?",
            "Do you working out regularly?"
          ],
          a: "Do you work out regularly?",
          successtip: "✓ HABIT QUESTION!\nDo you work out? = asking about habit 💪\nPresent simple for routines!"
        },
        {
          type: "blank",
          q: "Present perfect: 'I ___ every day this week!' (have worked out)",
          a: "have worked out",
          accept: { ignoreCase: true, alts: ["'ve worked out"] },
          successtip: "✓ ACHIEVEMENT!\nHave worked out = completed action! 🎉\nPresent perfect success!"
        },
        {
          type: "mc",
          q: "'Let's chill out at the café.' What does this suggest?",
          options: [
            "Let's relax and spend time at the café",
            "Let's make the café cold",
            "Let's leave the café"
          ],
          a: "Let's relax and spend time at the café",
          successtip: "✓ SUGGESTION!\nLet's chill out = casual invitation ☕\nRelaxed social time!"
        },
        {
          type: "order",
          q: "Traduce: Ella está haciendo ejercicio en este momento",
          a: "She is working out right now",
          successtip: "✓ CURRENT ACTION!\nIs working out = happening now! 🏋️‍♀️\nPresent continuous perfect!"
        },
        {
          type: "mc",
          q: "Complete the dialogue: A: 'You look stressed!' B: 'I know, I really need to ___.'",
          options: [
            "chill out",
            "work out",
            "turn up"
          ],
          a: "chill out",
          tip: "🎯 CONTEXT CLUE!\n\nWhen someone is STRESSED:\n✅ 'Chill out' = relax (perfect response)\n\n'Work out' could help, but doesn't directly address being stressed right now.\n\n'Chill out' = immediate relaxation\n'Work out' = exercise (different purpose)\n\nContext determines best choice! 😌",
          successtip: "✓ STRESS RELIEF!\nChill out = perfect for stress! 😌\nContext-appropriate choice!"
        },
        {
          type: "mc",
          q: "Which shows INCORRECT usage?",
          options: [
            "She work outs three times weekly",
            "She works out three times weekly",
            "She has worked out three times weekly"
          ],
          a: "She work outs three times weekly",
          successtip: "✓ ERROR SPOTTED!\n'Work outs' = wrong ❌\n'Works out' = correct ✅\nNever change the particle!"
        },
        {
          type: "blank",
          q: "Negative: 'I ___ on Mondays - it's my rest day.' (don't work out)",
          a: "don't work out",
          accept: { ignoreCase: true, alts: ["do not work out"] },
          successtip: "✓ REST DAY!\nDon't work out = negative form 🛋️\nSmart rest strategy!"
        },
        {
          type: "mc",
          q: "'I'm too tired to work out today.' What tense is 'work out' here?",
          options: [
            "Infinitive (base form)",
            "Present simple",
            "Present continuous"
          ],
          a: "Infinitive (base form)",
          successtip: "✓ INFINITIVE FORM!\n'To work out' = infinitive after 'too tired' ✅\nBase form with 'to'!"
        },
        {
          type: "order",
          q: "Traduce: Vamos a relajarnos en casa esta noche",
          a: "Let's chill out at home tonight",
          successtip: "✓ EVENING PLANS!\nLet's chill out = suggestion! 🏠😌\nInformal invitation perfect!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'My fitness goal is to ___ five times a week, but I also make sure to ___ on weekends.'",
          options: [
            "work out / chill out",
            "chill out / work out",
            "meet up / turn up"
          ],
          a: "work out / chill out",
          successtip: "✓ BALANCED GOAL!\nWork out (exercise) + chill out (rest) = healthy balance! ⚖️💪😌"
        }
      ]
    },

    // ========================================================================
    // SET 5: MEET UP & TURN UP - SOCIAL VERBS
    // ========================================================================
    {
      id: "m7-u3-s5",
      title: "Set 5: Meet Up & Turn Up",
      questions: [
        {
          type: "mc",
          q: "'Meet up' means:",
          options: [
            "To get together or arrange to meet socially",
            "To meet someone for the first time",
            "To go upstairs for a meeting"
          ],
          a: "To get together or arrange to meet socially",
          tip: "👥 MEET UP!\n\n'Meet up' = reunirse (social, casual)\n\nDifference from 'meet':\n• 'Meet' = encontrarse (general, first time)\n'Nice to meet you' = first meeting\n\n• 'Meet up' = reunirse (preplanned, social)\n'Let's meet up for coffee' = casual get-together\n\n'Meet up' implies:\n✓ Preplanned\n✓ Social/casual\n✓ Usually friends\n\nMore specific than just 'meet'! 🎯",
          successtip: "✓ SOCIAL VERB!\nMeet up = reunirse casualmente 👥\nFriendly, planned meeting!"
        },
        {
          type: "blank",
          q: "We ___ every Thursday for dinner. (meet up)",
          a: "meet up",
          accept: { ignoreCase: true },
          successtip: "✓ WEEKLY TRADITION!\nMeet up = regular social meeting 🍽️\nPresent simple for habit!"
        },
        {
          type: "mc",
          q: "'Turn up' can mean:",
          options: [
            "To arrive or appear (often unexpectedly)",
            "To turn a page upward",
            "To increase volume"
          ],
          a: "To arrive or appear (often unexpectedly)",
          successtip: "✓ ARRIVAL VERB!\nTurn up = aparecer/llegar 🚪\nOften implies surprise!"
        },
        {
          type: "order",
          q: "Traduce: Ellos se reunieron en el parque ayer",
          a: "They met up at the park yesterday",
          successtip: "✓ PAST MEETING!\nMet up = past tense ✅\nIrregular verb 'meet' → 'met'!"
        },
        {
          type: "mc",
          q: "Complete: 'He always ___ late to parties.'",
          options: [
            "turns up",
            "turn ups",
            "turning up"
          ],
          a: "turns up",
          successtip: "✓ LATE ARRIVAL!\nTurns up = third person ⏰\nVerb changes, particle doesn't!"
        },
        {
          type: "blank",
          q: "Question form: '___ we ___ tomorrow?' (Shall / meet up)",
          a: "Shall, meet up",
          accept: { ignoreCase: true, alts: ["shall / meet up"] },
          successtip: "✓ MAKING PLANS!\nShall we meet up? = polite suggestion 📅\nBase form after modal!"
        },
        {
          type: "mc",
          q: "'She never showed up' could also be said as:",
          options: [
            "She never turned up",
            "She never met up",
            "She never worked out"
          ],
          a: "She never turned up",
          tip: "🔄 SYNONYMS!\n\n'Turn up' ≈ 'Show up' = both mean 'aparecer/llegar'\n\nBoth are informal:\n• 'He didn't turn up' = No apareció\n• 'He didn't show up' = No se presentó\n• 'He didn't arrive' = No llegó (more formal)\n\n'Turn up' often implies:\n→ Expected but didn't come\n→ Informal situation\n→ Sometimes unexpected arrival\n\nVery common in casual English! 🎯",
          successtip: "✓ SYNONYM!\nTurn up = show up = appear! 🔄\nInterchangeable phrasal verbs!"
        },
        {
          type: "order",
          q: "Traduce: ¿Vas a reunirte con tus amigos este fin de semana?",
          a: "Are you going to meet up with your friends this weekend?",
          successtip: "✓ FUTURE PLANS!\nGoing to meet up = planned meeting 👥📅\nFuture with phrasal verb!"
        },
        {
          type: "mc",
          q: "Complete the story: 'We planned to ___ at 7 PM, but John didn't ___ until 8:30.'",
          options: [
            "meet up / turn up",
            "turn up / meet up",
            "work out / chill out"
          ],
          a: "meet up / turn up",
          successtip: "✓ BOTH VERBS!\nMeet up (plan) + turn up (arrive) = perfect! ⏰\nTwo different meanings!"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "They met ups yesterday",
            "They met up yesterday",
            "They are meeting up tomorrow"
          ],
          a: "They met ups yesterday",
          successtip: "✓ ERROR CAUGHT!\n'Met ups' = wrong ❌\n'Met up' = correct ✅\nParticle never changes!"
        }
      ]
    },

    // ========================================================================
    // SET 6: MORE MEET UP & TURN UP PRACTICE
    // ========================================================================
    {
      id: "m7-u3-s6",
      title: "Set 6: Advanced Social Phrasal Verbs",
      questions: [
        {
          type: "mc",
          q: "Most natural invitation: 'Want to ___ for lunch sometime?'",
          options: [
            "meet up",
            "turn up",
            "work out"
          ],
          a: "meet up",
          successtip: "✓ CASUAL INVITATION!\n'Meet up for lunch' = friendly suggestion! 🍽️👥"
        },
        {
          type: "blank",
          q: "Present perfect: 'He ___ to three parties this month without being invited!' (has turned up)",
          a: "has turned up",
          accept: { ignoreCase: true, alts: ["'s turned up"] },
          successtip: "✓ REPEATED ACTION!\nHas turned up = multiple times! 🎉\nUnexpected arrivals!"
        },
        {
          type: "mc",
          q: "'I'll meet up with you after work.' What tense is this?",
          options: [
            "Future (will + base form)",
            "Present simple",
            "Present continuous"
          ],
          a: "Future (will + base form)",
          successtip: "✓ FUTURE PROMISE!\nWill meet up = future plan! 📅\nModal + base verb + particle!"
        },
        {
          type: "order",
          q: "Traduce: Ella apareció en la fiesta sin avisar",
          a: "She turned up at the party without notice",
          successtip: "✓ SURPRISE ARRIVAL!\nTurned up = unexpected! 🎉😲\nPast tense phrasal verb!"
        },
        {
          type: "mc",
          q: "Complete: 'Let's ___ next week and catch up on everything!'",
          options: [
            "meet up",
            "turn up",
            "chill out"
          ],
          a: "meet up",
          tip: "👥 MEET UP CONTEXT!\n\n'Meet up' is perfect when:\n✓ Making social plans\n✓ Reconnecting with friends\n✓ Casual, friendly context\n\n'Meet up and catch up' = common combination!\n• Meet up = reunirse físicamente\n• Catch up = ponerse al día (conversation)\n\nBoth phrasal verbs often used together! 🎯",
          successtip: "✓ REUNION PLAN!\nMeet up + catch up = perfect combo! 👥💬\nSocial phrasal verbs together!"
        },
        {
          type: "mc",
          q: "Which shows correct negation?",
          options: [
            "He doesn't usually turn up on time",
            "He don't usually turn up on time",
            "He doesn't usually turns up on time"
          ],
          a: "He doesn't usually turn up on time",
          successtip: "✓ NEGATIVE FORM!\nDoesn't turn up = correct! ⏰\nBase form after doesn't!"
        },
        {
          type: "blank",
          q: "Continuous form: 'We ___ with the team tomorrow evening.' (are meeting up)",
          a: "are meeting up",
          accept: { ignoreCase: true, alts: ["'re meeting up"] },
          successtip: "✓ ARRANGED MEETING!\nAre meeting up = scheduled plan! 📅\nPresent continuous for arrangements!"
        },
        {
          type: "mc",
          q: "'Nobody turned up to the meeting.' This means:",
          options: [
            "No one came to the meeting",
            "Everyone came to the meeting",
            "The meeting was cancelled"
          ],
          a: "No one came to the meeting",
          successtip: "✓ ABSENCE!\nNo one turned up = nobody came 🚫\nNegative situation!"
        },
        {
          type: "order",
          q: "Traduce: ¿Dónde nos vamos a reunir?",
          a: "Where are we going to meet up?",
          successtip: "✓ LOCATION QUESTION!\nWhere + meet up = planning details! 📍\nFuture question with phrasal verb!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'We ___ every month, but last month nobody ___ because of the weather.'",
          options: [
            "meet up / turned up",
            "turn up / met up",
            "work out / chilled out"
          ],
          a: "meet up / turned up",
          successtip: "✓ CONTRAST!\nMeet up (plan) vs turned up (actual arrival) 🌧️\nBoth phrasal verbs in context!"
        }
      ]
    },

    // ========================================================================
    // SET 7: PHRASAL VERBS ACROSS TENSES
    // ========================================================================
    {
      id: "m7-u3-s7",
      title: "Set 7: Phrasal Verbs in Different Tenses",
      questions: [
        {
          type: "mc",
          q: "Present continuous: Which is CORRECT?",
          options: [
            "I am working out right now",
            "I am work outing right now",
            "I working out right now"
          ],
          a: "I am working out right now",
          tip: "📊 CONTINUOUS TENSE!\n\nStructure: BE + verb-ING + particle\n\n✅ 'Am working out' = correct\n'working' = verb with -ing\n'out' = particle (unchanged)\n\n❌ 'Work outing' = WRONG! Particle doesn't get -ing!\n\nRemember:\nOnly the VERB gets tense changes!\nParticle ALWAYS stays the same!\n\nThis applies to ALL tenses! 🎯",
          successtip: "✓ CONTINUOUS FORM!\nAm working out = happening now! 🏋️\nBE + verb-ing + particle!"
        },
        {
          type: "blank",
          q: "Past continuous: 'When you called, I ___ at the gym.' (was working out)",
          a: "was working out",
          accept: { ignoreCase: true },
          successtip: "✓ PAST ONGOING!\nWas working out = action in progress (past)! 💪\nPast continuous with phrasal verb!"
        },
        {
          type: "mc",
          q: "Future perfect: Which is CORRECT?",
          options: [
            "By next month, I will have worked out 100 times this year",
            "By next month, I will have work outed 100 times this year",
            "By next month, I will working out 100 times this year"
          ],
          a: "By next month, I will have worked out 100 times this year",
          successtip: "✓ FUTURE PERFECT!\nWill have worked out = completed future! 🎯\nComplex tense with phrasal verb!"
        },
        {
          type: "order",
          q: "Traduce (pasado continuo): Ellos se estaban reuniendo cuando empezó a llover",
          a: "They were meeting up when it started to rain",
          successtip: "✓ INTERRUPTED ACTION!\nWere meeting up = past continuous! 🌧️\nAction in progress interrupted!"
        },
        {
          type: "mc",
          q: "Present perfect continuous: Which is CORRECT?",
          options: [
            "She has been chilling out all afternoon",
            "She has been chill outing all afternoon",
            "She has chilling out all afternoon"
          ],
          a: "She has been chilling out all afternoon",
          tip: "⏱️ PERFECT CONTINUOUS!\n\nStructure: has/have been + verb-ING + particle\n\n✅ 'Has been chilling out' = correct\n'chilling' = verb with -ing\n'out' = particle (unchanged)\n\nThis tense emphasizes:\n→ Duration (all afternoon)\n→ Still relevant now\n→ Ongoing activity\n\nParticle NEVER changes, no matter how complex the tense! 🎯",
          successtip: "✓ DURATION TENSE!\nHas been chilling out = ongoing! 😌\nPerfect continuous + phrasal verb!"
        },
        {
          type: "blank",
          q: "Future continuous: 'This time tomorrow, I ___ at the beach.' (will be chilling out)",
          a: "will be chilling out",
          accept: { ignoreCase: true, alts: ["'ll be chilling out"] },
          successtip: "✓ FUTURE ONGOING!\nWill be chilling out = future in progress! 🏖️\nFuture continuous perfect!"
        },
        {
          type: "mc",
          q: "Past perfect: Which is CORRECT?",
          options: [
            "They had already met up before I arrived",
            "They had already meet upped before I arrived",
            "They had already meeting up before I arrived"
          ],
          a: "They had already met up before I arrived",
          successtip: "✓ PAST PERFECT!\nHad met up = earlier past action! ⏰\nPast participle + particle!"
        },
        {
          type: "mc",
          q: "Modal verb: 'You should ___ more often.'",
          options: [
            "work out",
            "working out",
            "worked out"
          ],
          a: "work out",
          successtip: "✓ MODAL + BASE FORM!\nShould work out = advice! 💪\nModal + infinitive (no 'to')!"
        },
        {
          type: "order",
          q: "Traduce (futuro perfecto): Para el viernes, habremos nos reunido tres veces esta semana",
          a: "By Friday, we will have met up three times this week",
          successtip: "✓ COMPLETED FUTURE!\nWill have met up = future perfect! 📅\nComplex tense mastered!"
        },
        {
          type: "mc",
          q: "Gerund: 'I enjoy ___ at outdoor gyms.'",
          options: [
            "working out",
            "work out",
            "worked out"
          ],
          a: "working out",
          successtip: "✓ GERUND FORM!\nWorking out = verb-ing as noun! 🏋️\nAfter 'enjoy', use gerund!"
        }
      ]
    },

    // ========================================================================
    // SET 8: MORE TENSE PRACTICE
    // ========================================================================
    {
      id: "m7-u3-s8",
      title: "Set 8: Mastering All Tenses",
      questions: [
        {
          type: "mc",
          q: "Conditional: 'If I had more time, I ___ every day.'",
          options: [
            "would work out",
            "will work out",
            "worked out"
          ],
          a: "would work out",
          tip: "🔮 CONDITIONAL MOOD!\n\nSecond conditional: If + past, would + base\n\n✅ 'Would work out' = hypothetical\n'If I had time' = I don't have time (unreal)\n'Would work out' = hypothetical result\n\nWith phrasal verbs:\nWould + base verb + particle\n\nParticle stays same even in conditional! 🎯",
          successtip: "✓ CONDITIONAL PERFECT!\nWould work out = hypothetical! 💭\nModal + base + particle!"
        },
        {
          type: "blank",
          q: "Used to: 'We ___ every weekend when we were younger.' (used to meet up)",
          a: "used to meet up",
          accept: { ignoreCase: true },
          successtip: "✓ PAST HABIT!\nUsed to meet up = repeated past action! 🔙\nPast habitual form!"
        },
        {
          type: "mc",
          q: "Passive voice: 'The meeting ___ by everyone.'",
          options: [
            "was turned up to (was attended)",
            "was turned up",
            "turned up"
          ],
          a: "was turned up to (was attended)",
          successtip: "✓ PASSIVE FORM!\nWas turned up to = passive! 📋\nRare but possible with phrasal verbs!"
        },
        {
          type: "order",
          q: "Traduce (imperativo): Relájate y deja de preocuparte",
          a: "Chill out and stop worrying",
          successtip: "✓ COMMAND FORM!\nChill out = imperative! 😌\nBase form as command!"
        },
        {
          type: "mc",
          q: "Infinitive with 'to': 'I need ___ before the wedding.'",
          options: [
            "to work out",
            "working out",
            "work out"
          ],
          a: "to work out",
          successtip: "✓ TO-INFINITIVE!\nTo work out = after 'need'! 💪\nInfinitive with 'to'!"
        },
        {
          type: "blank",
          q: "Going to future: 'They ___ at the new restaurant tonight.' (are going to meet up)",
          a: "are going to meet up",
          accept: { ignoreCase: true, alts: ["'re going to meet up"] },
          successtip: "✓ PLANNED FUTURE!\nGoing to meet up = definite plan! 🍽️\nFuture intention!"
        },
        {
          type: "mc",
          q: "Tag question: 'She works out daily, ___?'",
          options: [
            "doesn't she",
            "does she",
            "isn't she"
          ],
          a: "doesn't she",
          successtip: "✓ TAG QUESTION!\nDoesn't she = negative tag! ❓\nPositive statement → negative tag!"
        },
        {
          type: "mc",
          q: "Reported speech: 'He said he ___ at the gym yesterday.'",
          options: [
            "had worked out",
            "has worked out",
            "is working out"
          ],
          a: "had worked out",
          tip: "📣 REPORTED SPEECH!\n\nWhen reporting past events:\nPresent → Past\nPast → Past Perfect\n\nDirect: 'I worked out yesterday'\nReported: He said he HAD worked out\n\nWith phrasal verbs:\n• Had + past participle + particle\n• 'Had worked out' = correct\n\nParticle still doesn't change! 🎯",
          successtip: "✓ REPORTED SPEECH!\nHad worked out = backshift! 💬\nPast perfect in reported form!"
        },
        {
          type: "order",
          q: "Traduce (presente perfecto continuo): He estado haciendo ejercicio durante dos horas",
          a: "I have been working out for two hours",
          successtip: "✓ DURATION EMPHASIS!\nHave been working out = ongoing! ⏱️💪\nPerfect continuous complete!"
        },
        {
          type: "mc",
          q: "Final tense check: Identify all correct forms of 'work out':",
          options: [
            "works out / worked out / will work out / is working out / has worked out",
            "work outs / work outed / will works out / is work outing / has work outed",
            "working out / works outs / will working out / is worked out / have work out"
          ],
          a: "works out / worked out / will work out / is working out / has worked out",
          successtip: "✓ ALL TENSES MASTERED! 🌟\nAll forms correct = complete understanding!\nParticle NEVER changes!"
        }
      ]
    },

    // ========================================================================
    // SET 9: MORE PHRASAL VERBS & INTEGRATION
    // ========================================================================
    {
      id: "m7-u3-s9",
      title: "Set 9: Additional Phrasal Verbs",
      questions: [
        {
          type: "mc",
          q: "'Hang out' means:",
          options: [
            "To spend time together casually",
            "To hang clothes outside",
            "To wait outside"
          ],
          a: "To spend time together casually",
          tip: "👥 HANG OUT!\n\n'Hang out' = pasar el rato (casual)\n\nVery informal, friendly:\n• 'Let's hang out' = Salgamos/Pasemos tiempo juntos\n• 'I'm hanging out with friends' = Estoy con amigos\n\nDifference from 'meet up':\n• 'Meet up' = specific plan to get together\n• 'Hang out' = casual, no specific plan\n\nBoth social, but 'hang out' more relaxed! 😎",
          successtip: "✓ CASUAL SOCIAL!\nHang out = pasar el rato 😎\nVery informal phrasal verb!"
        },
        {
          type: "blank",
          q: "I ___ with my friends every Saturday. (hang out)",
          a: "hang out",
          accept: { ignoreCase: true },
          successtip: "✓ WEEKEND ROUTINE!\nHang out = casual time together! 👥\nRegular social activity!"
        },
        {
          type: "mc",
          q: "'Catch up' (in social context) means:",
          options: [
            "To talk and share recent news",
            "To run faster and catch someone",
            "To grab something falling"
          ],
          a: "To talk and share recent news",
          successtip: "✓ SOCIAL CATCH UP!\nCatch up = ponerse al día 💬\nShare news and updates!"
        },
        {
          type: "order",
          q: "Traduce: Necesitamos ponernos al día, hace mucho que no hablamos",
          a: "We need to catch up, we haven't talked in a long time",
          successtip: "✓ RECONNECTING!\nCatch up = share updates! 📞\nSocial phrasal verb!"
        },
        {
          type: "mc",
          q: "'Get up' vs 'Wake up' - what's the difference?",
          options: [
            "'Wake up' = open your eyes; 'Get up' = leave the bed",
            "They mean exactly the same thing",
            "'Wake up' is wrong, always use 'get up'"
          ],
          a: "'Wake up' = open your eyes; 'Get up' = leave the bed",
          tip: "⏰ WAKE UP VS GET UP!\n\nSubtle difference:\n\n'WAKE UP' = despertar (abrir los ojos)\n• 'I wake up at 7 AM' = Despierto a las 7\n• Still in bed!\n\n'GET UP' = levantarse (salir de la cama)\n• 'I get up at 7:30' = Me levanto a las 7:30\n• Out of bed, starting day\n\nSequence:\n1. Wake up (eyes open) 👁️\n2. Get up (leave bed) 🚶\n\nBoth phrasal verbs, different actions! 🎯",
          successtip: "✓ SUBTLE DIFFERENCE!\nWake up (eyes) → Get up (leave bed) 🛏️\nSequential actions!"
        },
        {
          type: "blank",
          q: "Present continuous: 'She ___ at the mall right now.' (is hanging out)",
          a: "is hanging out",
          accept: { ignoreCase: true, alts: ["'s hanging out"] },
          successtip: "✓ CURRENT ACTIVITY!\nIs hanging out = right now! 🛍️\nContinuous with phrasal verb!"
        },
        {
          type: "mc",
          q: "Complete: 'I ___ at 6 AM but I don't ___ until 6:30.'",
          options: [
            "wake up / get up",
            "get up / wake up",
            "turn up / meet up"
          ],
          a: "wake up / get up",
          successtip: "✓ MORNING SEQUENCE!\nWake up (first) → Get up (second) ⏰\nLogical order perfect!"
        },
        {
          type: "mc",
          q: "'Let's hang out sometime!' This is:",
          options: [
            "A casual invitation to spend time together",
            "A request to hang clothes",
            "A formal meeting request"
          ],
          a: "A casual invitation to spend time together",
          successtip: "✓ FRIENDLY INVITE!\nLet's hang out = casual invitation! 😊\nInformal social suggestion!"
        },
        {
          type: "order",
          q: "Traduce: Siempre me levanto temprano los días de semana",
          a: "I always get up early on weekdays",
          successtip: "✓ WEEKDAY ROUTINE!\nGet up = leave bed! 🌅\nAdverb + phrasal verb perfect!"
        },
        {
          type: "mc",
          q: "Which sentence uses multiple phrasal verbs correctly?",
          options: [
            "I wake up, get up, work out, then chill out",
            "I wake up, get up, work out, then chill outs",
            "I waking up, getting up, working out, then chilling out"
          ],
          a: "I wake up, get up, work out, then chill out",
          successtip: "✓ MULTIPLE PHRASAL VERBS! 🌟\nAll particles unchanged = perfect! ✅\nDaily routine with 4 phrasal verbs!"
        }
      ]
    },

    // ========================================================================
    // SET 10: NATURAL INTEGRATION & FLUENCY
    // ========================================================================
    {
      id: "m7-u3-s10",
      title: "Set 10: Natural Fluency with Phrasal Verbs",
      questions: [
        {
          type: "mc",
          q: "Most natural way to describe a typical day:",
          options: [
            "I wake up early, work out, meet up with colleagues, then chill out at home",
            "I wake up early, work out, meet up with colleagues, then chill outs at home",
            "I waking up early, working out, meeting up with colleagues, then chilling out at home"
          ],
          a: "I wake up early, work out, meet up with colleagues, then chill out at home",
          tip: "🌟 NATURAL FLOW!\n\nThis sentence uses 4 phrasal verbs naturally:\n\n1. Wake up (morning)\n2. Work out (exercise)\n3. Meet up (social/work)\n4. Chill out (evening)\n\nAll in present simple (habitual):\n• I wake up (not 'waking up')\n• I work out (not 'working out')\n• I meet up (not 'meeting up')\n• I chill out (not 'chilling out')\n\nAll particles unchanged!\n\nThis is how natives describe routines! 🎯",
          successtip: "✓ NATURAL ROUTINE!\n4 phrasal verbs in sequence! 🌟\nAll correctly conjugated!"
        },
        {
          type: "blank",
          q: "Complete the conversation: A: 'Want to hang out later?' B: 'Sure! Where should we ___?' (meet up)",
          a: "meet up",
          accept: { ignoreCase: true },
          successtip: "✓ NATURAL DIALOGUE!\nHang out + meet up = casual planning! 👥\nTwo social phrasal verbs together!"
        },
        {
          type: "mc",
          q: "Which shows INCORRECT usage?",
          options: [
            "She turn ups late and never works out",
            "She turns up late and never works out",
            "She turned up late and never worked out"
          ],
          a: "She turn ups late and never works out",
          successtip: "✓ ERROR FOUND!\n'Turn ups' = wrong ❌\n'Turns up' = correct ✅\nConsistency check passed!"
        },
        {
          type: "order",
          q: "Traduce: Después de despertarme, me levanto, hago ejercicio y luego me relajo",
          a: "After waking up, I get up, work out, and then chill out",
          successtip: "✓ COMPLEX SENTENCE!\n4 phrasal verbs in one sentence! 🌟\nGerund + three present verbs!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'On weekends, I usually ___ late, ___ at the gym, then ___ with friends in the evening.'",
          options: [
            "get up / work out / hang out",
            "get up / work out / hang outs",
            "getting up / working out / hanging out"
          ],
          a: "get up / work out / hang out",
          successtip: "✓ WEEKEND ROUTINE!\nThree phrasal verbs = perfect weekend! 🎉\nAll present simple for habits!"
        },
        {
          type: "mc",
          q: "Which sentence mixes tenses correctly?",
          options: [
            "I worked out yesterday, I'm working out now, and I'll work out tomorrow",
            "I worked out yesterday, I'm working out now, and I'll worked out tomorrow",
            "I work outed yesterday, I'm work outing now, and I'll work out tomorrow"
          ],
          a: "I worked out yesterday, I'm working out now, and I'll work out tomorrow",
          tip: "🎯 THREE TENSES!\n\nPast: 'worked out' (past simple)\nPresent: 'am working out' (continuous)\nFuture: 'will work out' (will + base)\n\nAll use same phrasal verb, different tenses!\n\nParticle 'out' NEVER changes:\n❌ 'work outed' = wrong\n❌ 'work outing' = wrong\n✅ 'worked out' / 'working out' / 'work out' = correct\n\nOnly the VERB changes! 🎯",
          successtip: "✓ THREE TENSES MASTERED! 🌟\nPast, present, future = all correct!\nParticle unchanged throughout!"
        },
        {
          type: "blank",
          q: "Advice format: 'You should ___ more and ___ less.' (work out / chill out)",
          a: "work out, chill out",
          accept: { ignoreCase: true, alts: ["work out / chill out"] },
          successtip: "✓ BALANCED ADVICE!\nWork out more + chill out less = ironic advice! 😄\nTwo phrasal verbs with should!"
        },
        {
          type: "mc",
          q: "Complete the story: 'Last Saturday, I ___ late, ___ with friends for brunch, then we ___ at the beach all afternoon before everyone ___ for the party.'",
          options: [
            "got up / met up / hung out / turned up",
            "get up / meet up / hang out / turn up",
            "got up / met up / hung out / turned ups"
          ],
          a: "got up / met up / hung out / turned up",
          successtip: "✓ PAST NARRATIVE! 📖\nFour phrasal verbs in past tense!\nAll correctly conjugated!"
        },
        {
          type: "order",
          q: "Traduce: Si tuviera más tiempo libre, me reuniría con mis amigos más seguido",
          a: "If I had more free time, I would meet up with my friends more often",
          successtip: "✓ CONDITIONAL COMPLEX!\nWould meet up = hypothetical! 💭\nPhrasal verb in conditional!"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: 'Every morning I ___ at 6, ___ at 6:30, and ___ for an hour. After work, I usually ___ with colleagues or ___ at home. On weekends, my friends and I ___, and sometimes someone unexpected ___ to join us!' How many phrasal verbs total?",
          options: [
            "Seven phrasal verbs: wake up, get up, work out, meet up, chill out, hang out, turns up",
            "Only three phrasal verbs",
            "They're not all phrasal verbs"
          ],
          a: "Seven phrasal verbs: wake up, get up, work out, meet up, chill out, hang out, turns up",
          tip: "🎉 COMPLETE UNIT MASTERY!\n\nThis paragraph includes:\n\n1. Wake up (morning)\n2. Get up (leave bed)\n3. Work out (exercise)\n4. Meet up (social)\n5. Chill out (relax)\n6. Hang out (casual time)\n7. Turns up (unexpected arrival)\n\nAll correctly conjugated:\n• Present simple for habits\n• Third person 'turns up' (not 'turn ups'!)\n• All particles unchanged\n\nALL Unit 3 concepts integrated:\n✅ Verb + Particle structure\n✅ Only verb changes\n✅ Different tenses\n✅ Natural usage\n\nThis is authentic, fluent English! 🌟",
          successtip: "🎉 UNIT 3 COMPLETELY MASTERED! 🎉\n\n✅ PHRASAL VERB STRUCTURE\nVerb + Particle = understood! 🔧\n\n✅ CONJUGATION RULE\nOnly VERB changes, particle NEVER! ✅\n\n✅ ESSENTIAL PHRASAL VERBS\n• Wake up, get up ⏰\n• Work out, chill out 💪😌\n• Meet up, turn up 👥\n• Hang out, catch up 😎💬\n\n✅ ALL TENSES MASTERED\n• Simple, continuous, perfect ✅\n• Future, conditional, modal ✅\n• Gerund, infinitive ✅\n\n✅ COMMON ERRORS AVOIDED\n• Never 'work outs' ❌\n• Never 'meet upped' ❌\n• Always correct! ✅\n\n🌟 YOU CAN NOW:\n• Use phrasal verbs naturally ✅\n• Conjugate across all tenses ✅\n• Avoid particle errors ✅\n• Sound like a native speaker! ✅\n\nB1-B2 phrasal verb mastery complete! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - A WEEKEND IN THE CITY (5 PASSAGES OF ~60 WORDS EACH)
  // ==========================================================================
  readingSet: {
    id: "m7-u3-reading",
    title: "Reading Practice - A Weekend in the City",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Friday Evening (~60 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**A Weekend in the City**

**Friday Evening**

I woke up excited on Friday morning, knowing the weekend was almost here. After work, I met up with my college friends at our favorite downtown café. We hadn't hung out together in months, so we had a lot to catch up on. Everyone turned up by 7 PM except Sarah, who arrived fashionably late at 7:30.`
      },
      {
        type: "mc",
        q: "In 'Everyone turned up by 7 PM,' the phrasal verb 'turned up' means:",
        options: [
          "Arrived",
          "Increased something",
          "Looked upward"
        ],
        a: "Arrived",
        successtip: "✓ PHRASAL VERB MEANING!\nTurned up = arrived 🚪\nCommon social context!"
      },
      {
        type: "mc",
        q: "How many phrasal verbs are used in this passage?",
        options: [
          "Four: woke up, met up, hung out, catch up, turned up",
          "Two",
          "None"
        ],
        a: "Four: woke up, met up, hung out, catch up, turned up",
        successtip: "✓ PHRASAL VERB COUNT!\n5 phrasal verbs in 60 words! 🎯\nNatural usage!"
      },

      // ======================================================================
      // BLOQUE 2: Saturday Morning (~60 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Saturday Morning**

I got up early on Saturday despite the late night. I always work out on weekend mornings - it's my favorite routine. The gym was packed with people who had the same idea. After working out for an hour, I felt energized. I went home, showered, and then chilled out with a good book for the rest of the morning.`
      },
      {
        type: "mc",
        q: "The text says 'I got up early.' Why is it 'got up' and not 'get up'?",
        options: [
          "Because it's past tense - the story happened yesterday",
          "It's a grammar mistake",
          "No particular reason"
        ],
        a: "Because it's past tense - the story happened yesterday",
        successtip: "✓ PAST TENSE!\nGot up = past simple ⏰\nVerb changes, particle stays!"
      },
      {
        type: "blank",
        q: "After exercising, the writer decided to relax. Which phrasal verb describes this?",
        a: "chilled out",
        accept: { ignoreCase: true, alts: ["chill out"] },
        successtip: "✓ RELAXATION VERB!\nChilled out = relaxed 😌\nPast tense phrasal verb!"
      },

      // ======================================================================
      // BLOQUE 3: Saturday Afternoon (~60 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Saturday Afternoon**

Around 2 PM, my friend Tom called. "Want to meet up?" he asked. We decided to hang out at the new outdoor market downtown. When we got there, we ran into three other friends who were also checking it out. We ended up spending the whole afternoon together, trying different food stalls and catching up on each other's lives.`
      },
      {
        type: "mc",
        q: "In this passage, 'meet up' and 'hang out' both appear. What's the subtle difference?",
        options: [
          "'Meet up' = specific plan to get together; 'hang out' = casual time spent together",
          "They mean exactly the same thing",
          "'Hang out' is incorrect grammar"
        ],
        a: "'Meet up' = specific plan to get together; 'hang out' = casual time spent together",
        tip: "🎯 SUBTLE DIFFERENCE!\n\n'MEET UP' = reunirse (specific meeting point/time)\n• 'Want to meet up?' = arrange specific meeting\n• Implies planning\n\n'HANG OUT' = pasar el rato (general casual time)\n• 'Let's hang out' = spend time together casually\n• More relaxed, less specific\n\nBoth social, but:\nMeet up → more specific\nHang out → more casual\n\nNatives use both naturally! 🌟",
        successtip: "✓ NUANCE UNDERSTOOD!\nMeet up (plan) vs hang out (casual) 👥\nSubtle difference mastered!"
      },
      {
        type: "mc",
        q: "'Catching up on each other's lives' means:",
        options: [
          "Sharing recent news and updates",
          "Running after each other",
          "Photographing each other"
        ],
        a: "Sharing recent news and updates",
        successtip: "✓ SOCIAL PHRASAL VERB!\nCatch up = share updates 💬\nReconnecting with friends!"
      },

      // ======================================================================
      // BLOQUE 4: Sunday Plans (~60 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Sunday Plans**

On Sunday, I planned to work out again in the morning, but I woke up feeling too tired. Instead, I got up slowly and decided to chill out all day. My roommate was meeting up with some people for brunch, but I passed. Sometimes you need a day to just relax and not turn up anywhere!`
      },
      {
        type: "mc",
        q: "The writer says 'I woke up feeling too tired.' Which phrasal verb describes what they did next?",
        options: [
          "Got up (slowly left the bed)",
          "Worked out (exercised)",
          "Met up (joined friends)"
        ],
        a: "Got up (slowly left the bed)",
        successtip: "✓ SEQUENCE!\nWoke up (eyes opened) → got up (left bed) 🛏️\nTwo different phrasal verbs!"
      },
      {
        type: "blank",
        q: "'Not turn up anywhere' means:",
        a: "not go anywhere",
        accept: { ignoreCase: true, alts: ["not appear anywhere", "not show up anywhere", "stay home"] },
        successtip: "✓ NEGATIVE USAGE!\nNot turn up = not appear/go 🏠\nStaying home phrasal verb!"
      },

      // ======================================================================
      // BLOQUE 5: Reflection + FINAL (~60 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Weekend Reflection**

Looking back, it was a perfect weekend balance. I worked out to stay healthy, met up with old friends, hung out in new places, and chilled out when I needed rest. Everyone turned up when expected, and we all got to catch up. These simple activities with phrasal verbs are how we actually live and communicate in everyday English!`
      },
      {
        type: "mc",
        q: "How many different phrasal verbs from Unit 3 appear in this final passage?",
        options: [
          "Six: worked out, met up, hung out, chilled out, turned up, catch up",
          "Two or three",
          "Only one"
        ],
        a: "Six: worked out, met up, hung out, chilled out, turned up, catch up",
        successtip: "✓ ALL VERBS INCLUDED!\n6 phrasal verbs in summary! 🎯\nUnit vocabulary integrated!"
      },
      {
        type: "mc",
        q: "Throughout this entire reading, were the particles (up, out) ever conjugated?",
        options: [
          "No - only the verbs changed (woke/got/met/hung/chilled/turned)",
          "Yes - sometimes they added -ed or -ing",
          "Yes - third person added -s to particles"
        ],
        a: "No - only the verbs changed (woke/got/met/hung/chilled/turned)",
        tip: "🎯 KEY GRAMMAR POINT!\n\nThroughout the reading:\n✅ Verbs changed: woke, got, met, hung, chilled, worked, turned\n❌ Particles NEVER changed: up, out (always the same!)\n\nExamples:\n• 'I woke up' (past)\n• 'I'm working out' (continuous)\n• 'She turns up' (third person)\n\nBut NEVER:\n❌ 'work outs'\n❌ 'meet upped'\n❌ 'hang outing'\n\nThis is the CRITICAL RULE of Unit 3! 🌟",
        successtip: "✓ GRAMMAR MASTERED! 🌟\nParticles NEVER change! ✅\nOnly verbs conjugate!"
      },
      {
        type: "mc",
        q: "FINAL QUESTION: This reading naturally integrated all major Unit 3 concepts. What did you notice?",
        options: [
          "All concepts: structure (verb+particle), conjugation (only verb changes), multiple tenses, natural usage",
          "Only basic present tense",
          "No clear grammar patterns"
        ],
        a: "All concepts: structure (verb+particle), conjugation (only verb changes), multiple tenses, natural usage",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU RECOGNIZED:\n\n✅ PHRASAL VERB STRUCTURE\nVerb + Particle in every example! 🔧\n\n✅ CONJUGATION RULE\nOnly verbs changed:\n• woke up, got up (past)\n• working out (continuous)\n• turns up (third person)\nParticles stayed: up, out ✅\n\n✅ ALL UNIT VOCABULARY\n• Wake up, get up ⏰\n• Work out 💪\n• Meet up, hang out 👥\n• Chill out 😌\n• Turn up, catch up 🚪💬\n\n✅ MULTIPLE TENSES\nPast, present, continuous - all correct! ⏱️\n\n✅ NATURAL CONTEXT\nReal weekend story = authentic usage! 🌟\n\n🌍 YOU CAN NOW:\n• Understand phrasal verbs in context ✅\n• Recognize conjugation patterns ✅\n• Use them naturally ✅\n• Read authentic English! ✅\n\nB1-B2 phrasal verb reading mastered! 📖💪"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 3 INTEGRATION
  // ==========================================================================
  // ==========================================================================
  // REVIEW SET - UNIT 3 INTEGRATION (COMPLETADO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u3-review",
    title: "⭐ Unit 3 Review: Everyday Activities",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which sentence shows CORRECT phrasal verb conjugation?",
        options: [
          "She works out every morning",
          "She work outs every morning",
          "She working out every morning"
        ],
        a: "She works out every morning",
        tip: "🔧 REVIEW: PHRASAL VERB RULE!\n\nOnly the VERB changes, NEVER the particle!\n\n✅ 'Works out' = verb gets -s for third person\n❌ 'Work outs' = WRONG! Particle can't change!\n\nThis is the #1 rule of Unit 3!\n\nPattern: Subject + VERB (conjugated) + PARTICLE (unchanged)\n\nApplies to ALL tenses! 🎯",
        successtip: "✓ CORRECT!\nFun fact: 'Work out' can also mean 'resolve a problem' → 'Things will work out!' 💡"
      },
      
      {
        type: "blank",
        q: "Past tense: 'Yesterday, they ___ at the new café.' (met up)",
        a: "met up",
        accept: { ignoreCase: true },
        successtip: "✓ PERFECT!\nSlang variation: 'link up' = same meaning, very informal → 'Let's link up later!' 📱"
      },
      
      {
        type: "mc",
        q: "'Turn up' most commonly means:",
        options: [
          "To arrive or appear",
          "To turn a page upward",
          "To increase temperature"
        ],
        a: "To arrive or appear",
        successtip: "✓ CORRECT!\nBritish slang: 'Turn up' can also mean 'act crazy/wild' → 'They really turned up at the party!' 🎉"
      },
      
      {
        type: "order",
        q: "Traduce: Ella está haciendo ejercicio en el gimnasio ahora mismo",
        a: "She is working out at the gym right now",
        successtip: "✓ EXCELLENT!\nGym slang: 'hit the gym' = go work out → 'I'm gonna hit the gym after work!' 🏋️"
      },
      
      {
        type: "mc",
        q: "Which shows INCORRECT particle usage?",
        options: [
          "I meet upped with them yesterday",
          "I met up with them yesterday",
          "I'm meeting up with them tomorrow"
        ],
        a: "I meet upped with them yesterday",
        successtip: "✓ ERROR SPOTTED!\nRelated phrasal verb: 'catch up with' = meet + share news → Often used together! 💬"
      },
      
      {
        type: "blank",
        q: "Complete: 'You should ___ and stop worrying.' (chill out)",
        a: "chill out",
        accept: { ignoreCase: true },
        successtip: "✓ NICE!\nYounger slang: 'Netflix and chill' = relax watching shows (but can have romantic implication!) 📺😉"
      },
      
      {
        type: "mc",
        q: "'Wake up' vs 'Get up' - identify the correct sequence:",
        options: [
          "First you wake up (open eyes), then you get up (leave bed)",
          "First you get up, then you wake up",
          "They mean exactly the same thing"
        ],
        a: "First you wake up (open eyes), then you get up (leave bed)",
        successtip: "✓ SEQUENCE MASTERED!\nIdiomatic: 'Get up on the wrong side of the bed' = wake up in a bad mood! 😤🛏️"
      },
      
      {
        type: "order",
        q: "Traduce: ¿Vas a reunirte con ellos mañana?",
        a: "Are you going to meet up with them tomorrow?",
        successtip: "✓ FUTURE PERFECT!\nAlternative: 'catch you later' = informal goodbye implying future meet-up! 👋"
      },
      
      {
        type: "mc",
        q: "Complete all blanks correctly: 'I usually ___ at 6 AM, ___ for an hour, then ___ with colleagues for coffee.' (wake up / work out / meet up)",
        options: [
          "wake up / work out / meet up",
          "wake up / work out / meet ups",
          "waking up / working out / meeting up"
        ],
        a: "wake up / work out / meet up",
        successtip: "✓ ALL THREE CORRECT!\nMorning routine slang: 'rise and grind' = wake up and work hard! 💪☀️"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY: 'Every morning I ___ early, ___ at the gym, then ___ with colleagues. Sometimes my friend ___ unexpectedly, and we all ___ together afterward before everyone ___ for the day.' Identify correct forms:",
        options: [
          "wake up / work out / meet up / turns up / hang out / chills out",
          "wake up / work out / meet up / turn ups / hang out / chill out",
          "waking up / working out / meeting up / turning up / hanging out / chilling out"
        ],
        a: "wake up / work out / meet up / turns up / hang out / chills out",
        tip: "🌟 COMPLETE INTEGRATION!\n\nThis tests EVERYTHING:\n\n✅ Multiple phrasal verbs (6 different ones!)\n✅ Conjugation: 'turns up' & 'chills out' need -s (third person)\n✅ Present simple for habits\n✅ All particles unchanged (up, out)\n\nCommon errors to avoid:\n❌ 'turn ups' / 'chill outs' = WRONG\n❌ Gerunds for habits = WRONG\n\nAll verbs must be:\n• Present simple (habits)\n• Third person singular gets -s\n• Particles NEVER change\n\nThis is complete Unit 3 mastery! 🎯",
        successtip: "🎉 UNIT 3 COMPLETELY MASTERED! 🎉\n\n✅ PHRASAL VERB STRUCTURE\nVerb + Particle = mastered! 🔧\n\n✅ CONJUGATION RULE\nOnly verb changes = understood! ✅\n\n✅ ALL ESSENTIAL VERBS\nWake/get up, work/chill out, meet/turn up, hang out, catch up = fluent! 💪\n\n✅ ALL TENSES\nSimple, continuous, perfect, future, conditional = all correct! ⏱️\n\n✅ COMMON ERRORS AVOIDED\nNo 'work outs', no 'meet upped' = perfect! 🎯\n\n🌟 BONUS VOCABULARY:\n• 'Link up' = meet up (slang)\n• 'Hit the gym' = go work out\n• 'Netflix and chill' = relax (slang)\n• 'Catch you later' = see you soon\n• 'Rise and grind' = morning motivation\n• 'Turn up' (party) = act wild\n\nYou can now use phrasal verbs like a native! 🌍\n\nB1-B2 everyday activities mastered! 💪\n\n¡Excelente trabajo! Ready for Unit 4! 🚀"
      }
    ]
  }
};
